import { RepositoryFactory } from "@/repositories/RepositoryFactory";

const BlogRepository = RepositoryFactory.get("blog");
const state = {
  blogs: [],
  blogsTotal: null,
  blog: {
    id: null,
    title: "",
    keywords: "",
    description: "",
    image: "",
    link: "",
  },
  latestPublishedBlogs: [],
  imagesLoading: false,
};

const getters = {
  getBlogs: (state) => {
    return state.blogs;
  },
  getBlog: (state) => {
    return state.blog;
  },
  getTotalItems: (state) => {
    return state.blogsTotal;
  },
  getBlogsWithMissingImageIds: (state) => {
    return state.blogs.reduce((filtered, option) => {
      if (!("image" in option)) {
        filtered.push(option.id);
      }
      return filtered;
    }, []);
  },
};

const actions = {
  getPublishedBlogs({ commit, dispatch, state }, { pageNumber }) {
    BlogRepository.getPublishedBlogs({ page: pageNumber, size: 5 })
      .then((response) => {
        const blogs = response.data.results;
        commit("APPEND_BLOGS", blogs);
        const totalBlogs = response.data.total_blogs;
        commit("SET_TOTAL_ITEMS", totalBlogs);
        const totalPages = response.data.total_pages;
        commit("pagination/SET_TOTAL_PAGES", totalPages, { root: true });

        // load images after blog data is loaded
        dispatch("getMissingImages");
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(`message.notifications.blogs.getBlogsError`),
          },
          { root: true },
        );
      });
  },
  getBlogs({ commit, dispatch, state }, params = {}) {
    BlogRepository.getBlogs(params)
      .then((response) => {
        const blogs = response.data.results;
        const totalPages = response.data.total_pages;
        commit("pagination/SET_TOTAL_PAGES", totalPages, { root: true });
        commit("SAVE_BLOGS", blogs);
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(`message.notifications.blogs.getBlogError`),
          },
          { root: true },
        );
      });
  },
  getBlog({ dispatch, commit }, id) {
    return BlogRepository.getBlog(id)
      .then((response) => {
        const blog = response.data;
        commit("SAVE_BLOG", blog);
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(`message.notifications.blogs.getBlogError`),
          },
          { root: true },
        );
      });
  },
  getMissingImages({ dispatch, commit, getters }) {
    commit("START_IMAGES_LOADING");

    const blogsWithMissingImageIds = getters.getBlogsWithMissingImageIds;
    const params = {
      blog_ids: blogsWithMissingImageIds,
    };

    BlogRepository.getMissingImages(params)
      .then((response) => {
        for (let blogContent of response.data.results) {
          commit("SET_BLOG_IMAGE", blogContent);
        }
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(`message.notifications.blogs.getBlogImageError`),
          },
          { root: true },
        );
      })
      .finally(() => {
        commit("END_IMAGES_LOADING");
      });
  },
  createBlog(context, blogData) {
    return BlogRepository.createBlog(blogData)
      .then(() => {
        context.dispatch(
          "notifications/addSuccess",
          {
            message: state.$t("message.notifications.blog.saveSuccess"),
          },
          { root: true },
        );
      })
      .catch((error) => {
        if (error.response && error.response.status === 413) {
          context.dispatch(
            "notifications/addError",
            {
              message: state.$t("message.notifications.blog.imageFailure"),
            },
            { root: true },
          );
        } else {
          context.dispatch(
            "notifications/addError",
            {
              message: state.$t("message.notifications.blog.saveFailure"),
            },
            { root: true },
          );
        }

        throw error;
      });
  },
  updateBlog({ dispatch, state }, blogData) {
    return BlogRepository.updateBlog(blogData.id, blogData)
      .then(() => {
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t("message.notifications.blog.updateSuccess"),
          },
          { root: true },
        );
      })
      .catch((error) => {
        if (error.response && error.response.status === 413) {
          dispatch(
            "notifications/addError",
            {
              message: state.$t("message.notifications.blog.imageFailure"),
            },
            { root: true },
          );
        } else {
          dispatch(
            "notifications/addError",
            {
              message: state.$t("message.notifications.blog.updateFailure"),
            },
            { root: true },
          );
        }
        throw error;
      });
  },
  deleteBlog({ dispatch, state }, id) {
    return BlogRepository.deleteBlog(id)
      .then(() => {
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t("message.notifications.blog.deleteSuccess"),
          },
          { root: true },
        );
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t("message.notifications.blog.deleteFailure"),
          },
          { root: true },
        );
        throw new Error();
      });
  },
};

const mutations = {
  APPEND_BLOGS(state, blogs) {
    state.blogs = [...state.blogs, ...blogs];
  },
  SET_TOTAL_ITEMS(state, totalItems) {
    state.blogsTotal = totalItems;
  },
  SAVE_LATEST_BLOGS(state, latestPublishedBlogs) {
    state.latestPublishedBlogs = latestPublishedBlogs;
  },
  SAVE_BLOGS(state, blogs) {
    state.blogs = blogs;
  },
  SAVE_BLOG(state, blog) {
    state.blog = blog;
  },
  RESET_BLOG_FORM(state) {
    state.blog = {
      id: null,
      title: "",
      keywords: "",
      description: "",
      image: "",
      link: "",
    };
  },
  SET_BLOG_IMAGE(state, blogData) {
    let i = state.blogs.findIndex((b) => b.id === blogData.id);

    if (i !== -1) {
      state.blogs[i].image = blogData.image;
    }
  },
  START_IMAGES_LOADING(state) {
    state.imagesLoading = true;
  },
  END_IMAGES_LOADING(state) {
    state.imagesLoading = false;
  },
};

export default {
  namespaced: true,
  getters,
  state,
  actions,
  mutations,
};
